import { canBeChoseong, getChoseong } from 'es-hangul'
import { sortBy } from 'lodash-es'
import { makeAutoObservable, runInAction } from 'mobx'

/** 문자열이 초성으로만 구성되어 있는지 확인 */
const isChoseongOnly = (str: string): boolean => {
  if (!str) return false
  return [...str].every((char) => canBeChoseong(char))
}

/** 초성 검색: target이 search 초성 패턴을 포함하는지 확인 */
const choseongIncludes = (target: string, search: string): boolean => {
  const targetChoseong = getChoseong(target)
  return targetChoseong.includes(search)
}

/** DualListBoxV2에서 사용하는 아이템의 기본 타입 (id, name 필수) */
type DualListBoxItem = {
  id: number | string
  name: string
}

type DualListBoxV2Options<V extends DualListBoxItem> = {
  /** 전체 아이템 목록 */
  items: V[]
  /** 오른쪽(선택됨)에 들어갈 초기 아이템 ID 목록 */
  initialRightItemIds?: Array<V['id']>
}

/**
 * Map 기반의 리스트 박스
 * - id를 키로 사용하여 O(1) 조회/추가/삭제
 * - 검색(일반 + 초성) 지원
 */
class ListBoxV2<V extends DualListBoxItem> {
  private map: Map<V['id'], V>

  constructor(items: V[]) {
    makeAutoObservable(this)
    this.map = new Map(items.map((item) => [item.id, item]))
  }

  get size() {
    return this.map.size
  }

  get isEmpty() {
    return this.size === 0
  }

  get isNotEmpty() {
    return this.size !== 0
  }

  /** 배열로 변환 */
  get toArr(): V[] {
    return [...this.map.values()]
  }

  /** 특정 필드 기준으로 정렬된 배열 반환 */
  toSortArrByKey<F extends keyof V>(field: F): V[] {
    return sortBy(this.toArr, (v) => v[field])
  }

  /**
   * 검색 (일반 텍스트 + 초성 검색 지원)
   * @param field - 검색할 필드명
   * @param searchValue - 검색어
   * @returns 필터링된 아이템 배열
   * @example
   * list.search('name', '김') // '김'이 포함된 아이템
   * list.search('name', 'ㄱ') // 초성 'ㄱ'으로 시작하는 아이템
   */
  search<F extends keyof V>(field: F, searchValue: string): V[] {
    const trimmedSearchValue = searchValue.trim()
    if (!trimmedSearchValue) return this.toArr
    const isChoseongSearch = isChoseongOnly(trimmedSearchValue)
    return this.toArr.filter((item) => {
      const fieldValue = String(item[field])
      if (isChoseongSearch) {
        return choseongIncludes(fieldValue, trimmedSearchValue)
      }
      return fieldValue.toLowerCase().includes(trimmedSearchValue.toLowerCase())
    })
  }

  has(id: V['id']): boolean {
    return this.map.has(id)
  }

  get(id: V['id']): V | undefined {
    return this.map.get(id)
  }

  add(item: V): void {
    this.map.set(item.id, item)
  }

  addMany(items: V[]): void {
    items.forEach((item) => this.add(item))
  }

  delete(id: V['id']): boolean {
    return this.map.delete(id)
  }

  deleteMany(ids: V['id'][]): void {
    ids.forEach((id) => this.delete(id))
  }

  clear(): void {
    this.map.clear()
  }

  keys(): IterableIterator<V['id']> {
    return this.map.keys()
  }

  values(): IterableIterator<V> {
    return this.map.values()
  }

  entries(): IterableIterator<[V['id'], V]> {
    return this.map.entries()
  }

  /** 주어진 ID 중 이 리스트에 존재하는 ID만 반환 */
  matchIds(ids: V['id'][]): V['id'][] {
    return ids.filter((id) => this.has(id))
  }
}

// ============================================================================
// DualListBoxV2: 좌/우 듀얼 리스트 박스 (변경 추적 기능 포함)
// ============================================================================

/**
 * 좌/우 듀얼 리스트 박스
 *
 * @description
 * - 왼쪽(미선택) ↔ 오른쪽(선택) 간 아이템 이동
 * - 초기 상태 대비 변경 추적 (movedToLeft, movedToRight)
 * - MobX observable로 UI 자동 업데이트
 *
 * @example
 * const dualList = new DualListBoxV2({
 *   items: allStudents,
 *   initialRightItemIds: [1, 2, 3] // 이미 선택된 학생 ID
 * })
 *
 * // 이동
 * dualList.moveLeftToRight([4, 5])
 *
 * // 변경 추적
 * dualList.movedToRight // 새로 추가된 ID: [4, 5]
 * dualList.isChanged    // true
 */
class DualListBoxV2<V extends DualListBoxItem> {
  leftList: ListBoxV2<V>
  rightList: ListBoxV2<V>
  /** 초기 오른쪽 아이템 ID Set (변경 추적용) */
  private initialRightIds: Set<V['id']>

  constructor({ items, initialRightItemIds = [] }: DualListBoxV2Options<V>) {
    makeAutoObservable(this)
    this.initialRightIds = new Set(initialRightItemIds)
    const leftItems = items.filter((item) => !this.initialRightIds.has(item.id))
    const rightItems = items.filter((item) => this.initialRightIds.has(item.id))
    this.leftList = new ListBoxV2(leftItems)
    this.rightList = new ListBoxV2(rightItems)
  }

  /** 초기 right → 현재 left로 이동한 ID 목록 (제거된 항목) */
  get movedToLeft(): Array<V['id']> {
    return [...this.initialRightIds].filter((id) => this.leftList.has(id))
  }

  /** 초기 left → 현재 right로 이동한 ID 목록 (추가된 항목) */
  get movedToRight(): Array<V['id']> {
    return [...this.rightList.keys()].filter((id) => !this.initialRightIds.has(id))
  }

  /** 초기 right → 현재 left로 이동한 아이템 목록 */
  get movedToLeftItems(): V[] {
    return this.movedToLeft.map((id) => this.leftList.get(id)).filter((item): item is V => !!item)
  }

  /** 초기 left → 현재 right로 이동한 아이템 목록 */
  get movedToRightItems(): V[] {
    return this.movedToRight.map((id) => this.rightList.get(id)).filter((item): item is V => !!item)
  }

  /** 초기 상태 대비 변경 여부 */
  get isChanged(): boolean {
    return this.movedToLeft.length > 0 || this.movedToRight.length > 0
  }

  /** 왼쪽 → 오른쪽으로 아이템 이동 */
  moveLeftToRight(ids: V['id'][]): void {
    runInAction(() => {
      ids.forEach((id) => {
        const item = this.leftList.get(id)
        if (item) {
          this.leftList.delete(id)
          this.rightList.add(item)
        }
      })
    })
  }

  /** 오른쪽 → 왼쪽으로 아이템 이동 */
  moveRightToLeft(ids: V['id'][]): void {
    runInAction(() => {
      ids.forEach((id) => {
        const item = this.rightList.get(id)
        if (item) {
          this.rightList.delete(id)
          this.leftList.add(item)
        }
      })
    })
  }

  /** 왼쪽 전체 → 오른쪽으로 이동 */
  moveLeftToRightAll(): void {
    this.moveLeftToRight([...this.leftList.keys()])
  }

  /** 오른쪽 전체 → 왼쪽으로 이동 */
  moveRightToLeftAll(): void {
    this.moveRightToLeft([...this.rightList.keys()])
  }

  /** 검색 결과에 해당하는 왼쪽 아이템만 오른쪽으로 이동 */
  moveFilteredLeftToRight<F extends keyof V>(field: F, searchValue: string): void {
    const filteredItems = this.leftList.search(field, searchValue)
    const ids = filteredItems.map((item) => item.id)
    this.moveLeftToRight(ids)
  }

  /** 주어진 ID 중 왼쪽 리스트에 존재하는 ID만 반환 */
  leftMatchIds(ids: V['id'][]): V['id'][] {
    return this.leftList.matchIds(ids)
  }

  /** 주어진 ID 중 오른쪽 리스트에 존재하는 ID만 반환 */
  rightMatchIds(ids: V['id'][]): V['id'][] {
    return this.rightList.matchIds(ids)
  }

  hasInLeft(id: V['id']): boolean {
    return this.leftList.has(id)
  }

  hasInRight(id: V['id']): boolean {
    return this.rightList.has(id)
  }

  /** 초기 상태에서 특정 ID를 제거 (onBeforeMoveToLeft 등 이동 전 콜백에서 사용) */
  removeFromInitialState(ids: Array<V['id']>): void {
    for (const id of ids) {
      this.initialRightIds.delete(id)
    }
  }
}

export default DualListBoxV2
